package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.dom.observers.ResizeObserver
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalMotionPolicy
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import dev.lingvo.site.i18n.tf

/** The host supplies the stage and brand panel; W2-10 can reuse its own brand and autosave owners. */
@Composable
internal fun StudioScreen(controller: StudioController, stage: @Composable () -> Unit, brand: @Composable () -> Unit) {
    val state = controller.uiState
    var width by remember { mutableStateOf(window.innerWidth) }
    var sheet by remember { mutableStateOf<String?>(null) }
    var tab by remember { mutableStateOf("layers") }
    var shortcuts by remember { mutableStateOf(false) }
    val panels = studioPanelLayout(width)
    val dispatch: (StudioIntent) -> Unit = { intent ->
        if (intent is StudioIntent.Action && intent.action.type == StudioActionType.SHORTCUTS) shortcuts = true
        else controller.dispatch(intent)
    }
    LaunchedEffect(Unit) { (document.querySelector("[data-studio-region='stage']") as? HTMLElement)?.focus() }
    val palette = ColorMode.current.toSitePalette()
    Div(attrs = {
        attr("data-studio-workspace", ""); attr("lang", document.documentElement?.getAttribute("lang") ?: "en")
        ref { element ->
            val observer = ResizeObserver { if (element.clientWidth > 0) width = element.clientWidth }
            observer.observe(element)
            if (element.clientWidth > 0) width = element.clientWidth
            onDispose { observer.disconnect() }
        }
        style { property("display", "grid"); property("height", "100%"); property("min-height", "0"); property("min-width", "0")
            property("grid-template-rows", "auto minmax(240px,1fr) ${if (width >= 640) "192px" else "320px"}"); property("max-width", "100%")
            property("overflow-x", "hidden"); property("overflow-y", "auto"); property("--studio-accent", palette.accent.toString()); property("color", palette.textPrimary.toString()); property("background", palette.surface.toString()) }
        onKeyDown { event ->
            if (event.isComposing || event.defaultPrevented) return@onKeyDown
            val target = event.target as? HTMLElement
            if (target?.closest("[role='dialog']") != null) return@onKeyDown
            val inField = target?.closest("input, textarea, select, [contenteditable='true']") != null
            val action = resolveStudioShortcut(StudioShortcutInput(event.code, event.key, event.ctrlKey, event.metaKey,
                event.altKey, event.shiftKey, textField = inField, stageFocused = target?.closest("[data-studio-region='stage']") != null))
                ?: return@onKeyDown
            event.preventDefault()
            if (action.type == StudioActionType.FOCUS_REGION) cycleStudioFocus(event.shiftKey)
            else dispatch(StudioIntent.Action(action))
        }
    }) {
        Div {
            StudioToolbar(state, dispatch)
            state.readOnlyReason?.let { Div(attrs = { attr("role", "note"); style { property("padding", Space.SM.toString()) } }) { Text(it) } }
            state.error?.let { Div(attrs = { attr("role", "alert"); style { property("padding", Space.SM.toString()); property("overflow-wrap", "anywhere") } }) { Text(it) } }
            state.fontStatus?.let { message ->
                Div(attrs = { attr("role", "status"); attr("data-studio-font-status", ""); style {
                    property("display", "flex"); property("align-items", "center"); property("flex-wrap", "wrap")
                    property("gap", Space.SM.toString()); property("padding", Space.SM.toString())
                } }) {
                    StudioMetaText(message)
                    if (state.fontFailed) StudioButton(t("studio.editor.studio_screen.retry_fonts", "Retry fonts")) { dispatch(StudioIntent.RetryFonts) }
                }
            }
        }
        Div(attrs = { style {
            property("display", "grid"); property("min-height", "0"); property("overflow", "hidden")
            property("grid-template-columns", if (panels.properties) "248px minmax(0,1fr) 288px" else if (panels.layers) "248px minmax(0,1fr)" else "minmax(0,1fr)")
        } }) {
            if (panels.layers) Div(attrs = { style { property("overflow-y", "auto"); property("padding", Space.SM.toString()) } }) {
                StudioButton(t("studio.editor.studio_screen.layers", "Layers")) { tab = "layers" }
                StudioButton(t("studio.editor.studio_screen.assets", "Assets")) { tab = "assets" }
                StudioButton(t("studio.editor.studio_screen.brand", "Brand")) { tab = "brand" }
                when (tab) { "layers" -> LayersPanel(state, dispatch); "assets" -> AssetsPanel(state, dispatch); else -> brand() }
            }
            Div(attrs = { style { property("display", "grid"); property("grid-template-rows", "auto minmax(0,1fr)"); property("position", "relative"); property("min-width", "0"); property("min-height", "0") } }) {
                Div(attrs = { style { property("display", "grid"); property("gap", Space.SM.toString()); property("padding", Space.XS.toString()) } }) {
                    Div(attrs = { style { property("display", "flex"); property("gap", Space.SM.toString()) } }) {
                    if (!panels.layers) StudioButton(t("studio.editor.studio_screen.open_layers", "Open layers")) { sheet = "layers" }
                    if (!panels.properties) StudioButton(t("studio.editor.studio_screen.open_properties", "Open properties")) { sheet = "properties" }
                    }
                    StageInputNotices(state, dispatch)
                }
                Div(attrs = { style { property("position", "relative"); property("min-height", "0"); property("min-width", "0") } }) {
                stage()
                }
            }
            if (panels.properties) Div(attrs = { style { property("overflow-y", "auto"); property("padding", Space.SM.toString()) } }) { PropertiesPanel(state, dispatch) }
        }
        Div(attrs = { attr("data-studio-bottom", ""); style {
            property("display", "grid"); property("min-width", "0"); property("min-height", "0"); property("overflow", "hidden")
            property("grid-template-columns", if (width >= 640) "minmax(0,1fr) 248px" else "minmax(0,1fr)")
            if (width < 640) property("grid-template-rows", "160px minmax(0,1fr)")
            property("gap", Space.MD.toString()); property("padding", Space.SM.toString())
            property("border-block-start", "1px solid ${palette.borderStrong}")
        } }) {
            Filmstrip(state, dispatch); LanguageRail(state, dispatch)
        }
        LModal(sheet != null, if (sheet == "layers") t("studio.editor.studio_screen.layers", "Layers") else t("studio.editor.studio_screen.properties", "Properties"),
            { sheet = null }, motionPolicy = LModalMotionPolicy.INSTANT) {
            if (sheet == "layers") LayersPanel(state, dispatch) else PropertiesPanel(state, dispatch)
        }
        StudioAnnouncement(state.announcement)
        ShortcutOverlay(shortcuts) { shortcuts = false }
    }
}

/** Region cycling follows the visible DOM and gives the layers tree its roving row focus. */
internal fun cycleStudioFocus(backwards: Boolean) {
    val order = listOf("toolbar", "layers", "stage", "properties", "filmstrip", "rail")
    val regions = order.mapNotNull { name -> document.querySelector("[data-studio-region='$name']") as? HTMLElement }
        .filter { it.getBoundingClientRect().width > 0 && it.getAttribute("aria-hidden") != "true" }
    if (regions.isEmpty()) return
    val current = regions.indexOfFirst { it.contains(document.activeElement) }
    val next = regions[(current + (if (backwards) -1 else 1) + regions.size) % regions.size]
    val focus = next.querySelector("[tabindex='0'], button:not(:disabled), input:not(:disabled), textarea:not(:disabled)") as? HTMLElement
    (focus ?: next).focus()
}

/** The canvas overload keeps the stage stable while its DOM mirror and panels follow committed snapshots. */
@Composable
internal fun StudioScreen(controller: StageController, brand: @Composable () -> Unit) {
    val state = controller.uiState
    val label = tf("studio.canvas_cell", "{shot}, {language}, {device}",
        "shot" to (state.slots.find { it.id == state.view.slotId }?.title ?: state.view.slotId),
        "language" to (state.languages.find { it.locale == state.view.locale }?.label ?: state.view.locale),
        "device" to state.view.device)
    StudioScreen(controller, stage = { CanvasStage(controller, label); StageDomOverlays(state, controller) }, brand = brand)
}
